import React, { useContext, useEffect, useState } from "react";
import { ethers } from "ethers";
import {
  VStack,
  Text,
  Box,
  Image,
  HStack,
  Stat,
  Spacer,
  Tooltip,
  IconButton,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import Reader from "./contracts/Reader.json";
import { dai, usdc_e, frax, usdc, usdt, weth, readerAddress, vaultAddress } from "../../config";
import { AppContext } from "../../AppContext";

const DisplayLiquidityValues = () => {
  const [liquidityData, setLiquidityData] = useState([]);
  const [error, setError] = useState(null);
  const { fastestWebSocket } = useContext(AppContext);

  const tokenImages = {
    usdc: "./usdc.png",
    usdc_e: "./usdc.png",
    usdt: "./usdt.png",
    frax: "https://assets.coingecko.com/coins/images/13422/standard/frax-logo.png",
    weth: "./weth.png",
    dai: "./dai.png",
  };

  const abi = Reader.abi;

  const formatRawValue = (value, decimals) => {
    if (!value) return "0.00";
    return parseFloat(ethers.utils.formatUnits(value, decimals)).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const fetchLiquidityValues = async () => {
    try {
      const provider = new ethers.providers.WebSocketProvider(fastestWebSocket);
      const contract = new ethers.Contract(readerAddress, abi, provider);
      const vault = ethers.utils.getAddress(vaultAddress);

      const tokens = [
        { name: "USDC", address: usdc, decimals: 6, image: tokenImages.usdc },
        { name: "USDC.e", address: usdc_e, decimals: 6, image: tokenImages.usdc_e },
        { name: "USDT", address: usdt, decimals: 6, image: tokenImages.usdt },
      //  { name: "FRAX", address: frax, decimals: 18, image: tokenImages.frax },
        { name: "DAI", address: dai, decimals: 18, image: tokenImages.dai },
      ];

      const liquidityPromises = tokens.map(async (token) => {
        const maxInWei = await contract.getMaxAmountIn(vault, weth, token.address);
        const maxOutWei = await contract.getAmountOut(vault, weth, token.address, maxInWei);
        return {
          name: token.name,
          image: token.image,
          maxIn: formatRawValue(maxInWei, 18),
          maxOut: formatRawValue(maxOutWei[0], token.decimals),
        };
      });

      const liquidityValues = await Promise.all(liquidityPromises);
      liquidityValues.sort((a, b) => parseFloat(b.maxOut.replace(/,/g, "")) - parseFloat(a.maxOut.replace(/,/g, "")));

      setLiquidityData(liquidityValues);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("An error occurred while fetching liquidity values.");
    }
  };

  useEffect(() => {
    fetchLiquidityValues();
  }, []);

  return (
    <Box h="100%" w="100%" minWidth='290px' fontFamily='Sentinel'>
      <HStack justify="space-between" mb={1} px={2}>
        <Text fontSize="13px" as="b">Liquidity Values</Text>
        <Tooltip label="Reload Liquidity" aria-label="reload" placement="top" hasArrow>
          <IconButton
            aria-label="Reload Liquidity"
            icon={<RepeatIcon />}
            colorScheme="blue"
            size="xs"
            variant="unstyled"
            onClick={fetchLiquidityValues}
          />
        </Tooltip>
      </HStack>

      {liquidityData.map((token) => (
        <Stat key={token.name} w="100%">
          <HStack bg="gray.50" w="100%" h="auto" px={2} py={1} borderBottom="1px solid silver" justify="space-between">
            {/* Token Image & Name */}
            <HStack>
              <Image width={4} height={4} src={token.image} alt={`${token.name} logo`} />
              <Text fontSize="sm">{token.name}</Text>
            </HStack>

            {/* Liquidity Value */}
            <Text fontSize="sm" fontWeight="bold">${token.maxOut}</Text>
          </HStack>
        </Stat>
      ))}
    </Box>
  );
};

export default DisplayLiquidityValues;
